import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { storeTokens, clearTokens, hasToken } from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    if (!hasToken()) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch {
      clearTokens();
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password, rememberMe) => {
    const { data } = await api.post('/auth/login', { email, password, remember_me: rememberMe });
    storeTokens(data.access_token, data.refresh_token, rememberMe);
    const userData = { id: data.id, email: data.email, name: data.name, role: data.role, active_list_id: data.active_list_id };
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password, passwordConfirm) => {
    const { data } = await api.post('/auth/register', { name, email, password, password_confirm: passwordConfirm });
    storeTokens(data.access_token, data.refresh_token, true);
    const userData = { id: data.id, email: data.email, name: data.name, role: data.role, active_list_id: data.active_list_id };
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch {}
    clearTokens();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
